/**
 * useAppPermission — Centralized hook for current user's app-level permission.
 *
 * Returns the effective permission level (Reader/Contributor) and derived
 * booleans (canRead, canEdit). Use this instead of scattered usePermission
 * calls to keep permission logic in one place.
 */
import { useMemo } from 'react';
import { useCheckAccess } from './useUserPermissions';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { useParams } from 'react-router-dom';
import type { AutomationAppUIModel } from '@/types/automation';

export type AppPermissionLevel = 'Reader' | 'Contributor' | 'Owner' | null;

export interface AppPermissionResult {
  /** The user's effective permission on this app; null while loading or when access is denied */
  permission: AppPermissionLevel;
  /** True if user has at least Reader access */
  canRead: boolean;
  /** True if user has Contributor access (can create, edit, delete content) */
  canEdit: boolean;
  /** True if user has Admin/Owner access */
  canDelete: boolean;
  /** True while the permission check is in flight */
  isLoading: boolean;
}

const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';
const isLocalMode = import.meta.env.VITE_LOCAL_MODE === 'true';
const FULL_ACCESS: AppPermissionResult = { permission: 'Owner', canRead: true, canEdit: true, canDelete: true, isLoading: false };

export interface UseAppPermissionOptions {
  /**
   * When false, suppresses the underlying access-check network request and returns
   * a non-loading "no permission" result. Use this to avoid firing a project-scope
   * access check from contexts that only care about app-scope permissions
   * (e.g., gating an app-scoped affordance on routes where no app is in scope).
   * Defaults to true.
   */
  enabled?: boolean;
}

export function useAppPermission(
  app?: AutomationAppUIModel,
  options?: UseAppPermissionOptions,
): AppPermissionResult {
  const { projectName, appName } = useParams<{ projectName: string; appName: string }>();
  const selectedApp = useAppStore((state) => state.selectedApp)
  const { account } = useAuth();
  const userId = useMemo(() => account?.localAccountId ?? null, [account]);
  const effectiveApp = useMemo(() => app ?? selectedApp, [app, selectedApp]);
  const effectiveAppName = useMemo(() => app ? app.name?.split('/').slice(-1)[0] : appName, [app, appName]);
  // In E2E/local mode we short-circuit to FULL_ACCESS below, so the access check
  // is unused. Suppress it in those modes so its (principal-less, "No access")
  // result cannot poison the shared checkAccess query cache that usePermission
  // consumers read from.
  const enabled = (options?.enabled ?? true) && !isE2EMode && !isLocalMode;

  const { data: accessResponse, isLoading } = useCheckAccess(
    projectName ?? '',
    effectiveAppName ?? '',
    /* userId */null,
    { enabled },
  );

  const isOwner = useMemo(() => effectiveApp?.properties?.createdBy?.toLowerCase() === userId?.toLowerCase(), [effectiveApp, userId]);

  return useMemo(() => {
    // In E2E/local mode there's no real auth — grant full access
    if (isE2EMode || isLocalMode) {
      return FULL_ACCESS;
    }

    // Caller opted out of the access check entirely (e.g., no app is in scope).
    // Return a stable no-permission result without surfacing a loading state so
    // the UI can render its disabled affordance immediately.
    if (!enabled) {
      return { permission: null, canRead: false, canEdit: false, canDelete: false, isLoading: false };
    }

    if (isLoading || !accessResponse) {
      return { permission: null, canRead: false, canEdit: false, canDelete: false, isLoading: true };
    }

    const role = accessResponse.role?.toLowerCase();

    if (isOwner) {
      return { permission: 'Owner', canRead: true, canEdit: true, canDelete: true, isLoading: false };
    }

    if (role === 'contributor') {
      return { permission: 'Contributor', canRead: true, canEdit: true, canDelete: false, isLoading: false };
    }

    if (role === 'reader') {
      return { permission: 'Reader', canRead: true, canEdit: false, canDelete: false, isLoading: false };
    }

    // No access or unknown role
    return { permission: null, canRead: false, canEdit: false, canDelete: false, isLoading: false };
  }, [accessResponse, isLoading, isOwner, enabled]);
}
